import { getSupportedPaymentMethodsForCaja } from '@seek/payments-widget';
import { Stack } from 'braid-design-system';
import { useState, type ComponentProps } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SystemBanner } from 'src/app/shared/components/SystemBanner';
import { JOB_PUBLISH_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { getProductCategories } from 'src/app/utils/analytics/utils/getProductCategories';
import { convertToCurrency } from 'src/app/utils/currency';
import { PRODUCT_EVENTS_PURCHASE } from 'src/app/v1/analytics';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { OrderSummary } from 'src/app/v1/views/components/OrderSummary';
import { PaymentsSection } from 'src/app/v1/views/pages/EditPayAndPost/sections/PaymentsSection';
import type { AppState, Tealium } from 'src/types/appState';

const getTealiumAttributes = (tealium: Tealium) => ({
  adProductSetId: tealium.productSetId,
  brandHasCoverImage: tealium.hasCoverImage,
  brandHasLogo: tealium.hasLogo,
  jobAdTypeSelected: [tealium.jobAdTypeSelected],
  jobIsLinkOut: Boolean(tealium.isLinkOutApplyViaSeek),
  productCategories: getProductCategories(tealium.adBudgetPurchased),
  productSetProductIds: tealium.productSetProductIds,
});

interface OldCheckoutSectionProps {
  handledCancelPayment: ComponentProps<
    typeof PaymentsSection
  >['handledCancelPayment'];
  onPaymentSuccess: ComponentProps<typeof PaymentsSection>['onPaymentSuccess'];
  resetError: () => void;
  setError: (error: Error | string) => void;
}

export const OldCheckoutSection = ({
  handledCancelPayment,
  onPaymentSuccess,
  resetError,
  setError,
}: OldCheckoutSectionProps) => {
  const {
    contractBalance,
    country,
    productTotalPrice,
    jobOrderId,
    seekAdvertiserId,
    ...tealiumRelatedAttributes
  } = useSelector(
    (state: AppState) => ({
      contractBalance: state.session.contractBalance,
      country: state.tealium.country,
      productTotalPrice: state.tealium.productTotalPrice,
      jobOrderId: state.session.jobOrderId,
      seekAdvertiserId: state.session.seekAdvertiserId,
      ...getTealiumAttributes(state.tealium),
    }),
    shallowEqual,
  );

  const [hidePaymentDetails, setHidePaymentDetails] = useState(false);

  const handleHidePaymentDetails = () => {
    setHidePaymentDetails(true);
  };

  const costDifference =
    Number(convertToCurrency(Number(contractBalance))) -
    Number(productTotalPrice);

  const isPaymentDetailsHidden =
    hidePaymentDetails ||
    Boolean(typeof contractBalance === 'number' && costDifference <= 0);

  const onPostAdButtonClicked = async () => {
    const countryId = country ?? 'AU';
    const paymentMethods = getSupportedPaymentMethodsForCaja(
      countryId.toUpperCase(),
    );

    const eventData = {
      eventName: JOB_PUBLISH_PRESSED,
      adStatus: 'open',
      paymentMethodShown: paymentMethods,
      productEvents: [PRODUCT_EVENTS_PURCHASE],
      productTotalPrice,
      ...tealiumRelatedAttributes,
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    resetError();

    if (!jobOrderId) {
      throw new Error('missing orderId');
    }
  };

  return (
    <Stack space="medium">
      <OrderSummary
        hidePaymentDetails={isPaymentDetailsHidden}
        handleHidePaymentDetails={handleHidePaymentDetails}
      />
      <SystemBanner context="job_posting_pay_and_post" style="alert" />
      {Boolean(seekAdvertiserId) && (
        <PaymentsSection
          onPaymentSuccess={onPaymentSuccess}
          onPaymentFailed={setError}
          handledCancelPayment={handledCancelPayment}
          onPostAdButtonClicked={onPostAdButtonClicked}
          onPostAdButtonClickFailed={setError}
        />
      )}
    </Stack>
  );
};
